import React, { useMemo } from 'react'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { Modal } from 'react-bootstrap'
import { MenuTable } from 'components/Tables/MenuTable'
import { DoorsModal } from 'components/NewCompanyPage/Modals/DoorsModal'
import {
  DescriptionAndContactInfo,
  Divider,
  ForecastingSection,
  MetricCard,
  StatsCard,
  AdditionalCharacteristics,
  SectionWithContent,
  Rating,
} from 'components/NewCompanyPage/Sidebar/shared'
import { AFbLink } from 'components/FbUI/FbLink/styles'
import { DoorsTable } from 'components/Tables/DoorsTable/DoorsTable'
import { FBPageTableContainer } from 'components/FbUI/Page/styles'
import useDocumentTitle from 'components/useDocumentTitle'
import { PredictedTrafficBadge } from 'components/PredictedTrafficBadge/PredictedTrafficBadge'
import { formatInteger } from 'utils/formatting'
import { useHideFooter } from 'context/layout'

export function NewChainPage({ chainID }: { chainID?: number }) {
  const api = apiService()
  const { data: chainData, isLoading } = api.useGetChain(chainID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(`${chainData?.chain || 'Not Found'} (Chain) - First Bite`)

  const [showMenuModal, setShowMenuModal] = React.useState(false)
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      chainData?.chain ? [{ title: 'Company', label: chainData.chain }] : [],
    [chainData?.chain]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  // Format HQ city from available data
  const formatHqCity = () => {
    const hqCity1 = chainData?.hq_city1
    const hqState1 = chainData?.hq_state1
    const hqCity2 = chainData?.hq_city2
    const hqState2 = chainData?.hq_state2

    if (hqCity1 && hqState1) {
      return `${hqCity1}, ${hqState1} - USA`
    }
    if (hqCity2 && hqState2) {
      return `${hqCity2}, ${hqState2} - USA`
    }
    if (hqCity1) {
      return `${hqCity1} - USA`
    }
    if (hqCity2) {
      return `${hqCity2} - USA`
    }
    return undefined
  }

  // Helper function to render predicted traffic badges
  const renderPredictedTraffic = (group: string) => {
    const badges = group.split(',').map((traffic) => traffic.trim())
    return (
      <div className="flex gap-1">
        {badges.map((badge, index) => (
          <PredictedTrafficBadge key={index} value={badge} size="small" />
        ))}
      </div>
    )
  }

  // Helper function to get Instagram handle from URL
  const getInstagramHandle = (url: string) => {
    try {
      const match = url.match(/instagram\.com\/([^/?]+)/)
      return match ? `@${match[1]}` : url
    } catch {
      return url
    }
  }

  // Helper function to render Instagram ranking badge using PredictedTrafficBadge
  const renderInstagramRanking = (textPercentile: string | null) => {
    return <PredictedTrafficBadge value={textPercentile} size="small" />
  }

  // Build stats items for the summary tab
  const stats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cuisine',
      value: chainData?.cuisine_50 || '-',
    },
    {
      label: 'Predicted Traffic',
      value: chainData?.velocity_group
        ? renderPredictedTraffic(chainData.velocity_group)
        : '-',
    },
    {
      label: 'Expense Category',
      value: chainData?.expense_category ? (
        <span className="font-medium text-green-700">
          {chainData.expense_category}
        </span>
      ) : (
        '-'
      ),
    },
    {
      label: 'Average Rating',
      value: chainData?.rating ? <Rating value={chainData.rating} /> : '-',
    },
    {
      label: 'Total Reviews',
      value: chainData?.reviews_count
        ? formatInteger(chainData.reviews_count)
        : '0',
    },
  ]

  // Build Instagram stats items - always show
  const instagramStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Instagram Handle',
      value: chainData?.instagram?.url ? (
        <AFbLink
          className="!text-xs"
          href={chainData.instagram.url}
          target="_blank"
        >
          {getInstagramHandle(chainData.instagram.url)}
        </AFbLink>
      ) : (
        '-'
      ),
    },
    {
      label: 'Instagram Posts',
      value: chainData?.instagram?.posts
        ? formatInteger(chainData.instagram.posts)
        : '0',
    },
    {
      label: 'Instagram Ranking',
      value: chainData?.instagram?.text_percentile
        ? renderInstagramRanking(chainData.instagram.text_percentile)
        : '-',
    },
    {
      label: 'Instagram Followers',
      value: chainData?.instagram?.followers
        ? formatInteger(chainData.instagram.followers)
        : '0',
    },
  ]

  // Build characteristics array
  const characteristics = [
    ...(chainData?.services || []),
    ...(chainData?.atmospheres || []),
    ...(chainData?.offerings || []),
    ...(chainData?.open_hours || []),
  ].filter(Boolean)

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      description={chainData?.description}
    />
  )

  // Metrics - always show
  summaryTabContent.push(
    <div key="metrics" className="grid grid-cols-2 gap-3">
      <MetricCard
        key="chain-size"
        value={formatInteger(chainData?.count || 0)}
        label="Chain Size"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
      <MetricCard
        key="menu-size"
        value={formatInteger(chainData?.menu_length || 0)}
        label="Chain Menu Size"
        actionText="View Menu"
        onActionClick={() => setShowMenuModal(true)}
      />
    </div>
  )

  // Stats Section - always show
  summaryTabContent.push(<StatsCard key="stats" stats={stats} />)
  summaryTabContent.push(<Divider key="divider-2" />)

  summaryTabContent.push(
    <StatsCard key="instagram-stats" stats={instagramStats} />
  )
  summaryTabContent.push(<Divider key="divider-3" />)

  // Forecasting Section
  summaryTabContent.push(
    <ForecastingSection
      key="forecasting"
      chainId={chainData?.contact_company_id}
    />
  )

  summaryTabContent.push(<Divider key="divider-4" />)

  summaryTabContent.push(
    <AdditionalCharacteristics
      key="characteristics"
      characteristics={characteristics}
    />
  )

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = [
    <MetricCard
      key="hq-city"
      value={formatHqCity() || 'N/A'}
      label="HQ City"
      variant="full-width"
    />,

    <Divider key="divider-2" />,

    <div key="summaries" className="space-y-9">
      <SectionWithContent
        key="city-summary"
        title="City Summary"
        content={chainData?.city_summary || 'N/A'}
      />
      <SectionWithContent
        key="state-summary"
        title="State Summary"
        content={chainData?.state_summary || 'N/A'}
      />
      <SectionWithContent
        key="zip-summary"
        title="Zip Summary"
        content={chainData?.zip_summary || 'N/A'}
      />
      <SectionWithContent
        key="dma-summary"
        title="DMA Summary"
        content={chainData?.dma_summary || 'N/A'}
      />
    </div>,
  ]

  return (
    <>
      <CompanyPageLayout
        chainProxyId={chainData!.contact_company_id!}
        breadcrumbRoot="Commercial - Restaurant Chain"
        breadcrumbs={[
          { title: 'Chain', breadcrumb: chainData!.chain!, url: undefined },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        sidebar={{
          header: {
            name: chainData!.chain!,
            domain: chainData!.domain!,
            logoUrl: chainData!.logo!,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={chainData!.contact_company_id!}
          entityID={chainID}
          companyName={chainData!.chain!}
          websiteAddress={chainData!.domain!}
          companyTypeSlug="restaurants-bars"
          tableTypeSlug="restaurants-bars"
        />
      </CompanyPageLayout>

      {/* Menu Modal */}
      <Modal
        show={showMenuModal}
        onHide={() => setShowMenuModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {chainData?.chain} Menu
            {chainData?.menu_length && (
              <span className="text-muted font-light ml-2">
                ({chainData.menu_length} items)
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MenuTable chainId={chainData?.id} />
        </Modal.Body>
      </Modal>

      {/* Doors Modal */}
      <DoorsModal
        show={showDoorsModal}
        onHide={() => setShowDoorsModal(false)}
        chainData={{
          id: chainData!.id!,
          name: chainData!.chain!,
          domain: chainData!.domain!,
          contact_company_id: chainData!.contact_company_id!,
          count: chainData!.count!,
        }}
        chainID={chainID}
        forChainProxyId={chainData!.contact_company_id!}
        chainName={chainData!.chain!}
        subCategory={{
          name: 'Restaurant Chain',
          slug: 'restaurants-bars',
          description: 'Restaurant Chain',
        }}
        matchedTableComponent={(props) => (
          <FBPageTableContainer
            style={{
              height: '540px',
              width: '100%',
            }}
          >
            <DoorsTable
              tableKeyForColumnOrdering="chain-doors-table"
              hideTitle={true}
              disableUrlPagination
              chainFilter={chainID}
              {...props}
            />
          </FBPageTableContainer>
        )}
        unmatchedTableKeyPrefix="unmatched-doors-table"
      />
    </>
  )
}
